import { useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";
import SEO from "@/components/SEO";
import { useReveals } from "@/components/Reveal";

const INTERESTS = [
  "Shipping & Logistics",
  "Software Solutions",
  "Data Center — EuroVDC",
  "Vessel — MV ARTAX",
  "Hospitality — Paros Resort",
  "General Enquiry",
];

const inputCls =
  "w-full border-b border-white/20 bg-transparent py-3 font-sans text-base text-bone placeholder:text-smoke/50 focus:border-amber focus:outline-none transition-colors duration-300";

export default function Contact() {
  useReveals();
  const [form, setForm] = useState({ name: "", company: "", email: "", interest: INTERESTS[0], message: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    const subject = `Website enquiry — ${form.interest}`;
    const body = `Name: ${form.name}\nCompany: ${form.company}\nEmail: ${form.email}\nInterest: ${form.interest}\n\n${form.message}`;
    window.location.href = `mailto:info@oaholding.co?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    toast.success("Opening your email client — your enquiry is addressed to info@oaholding.co");
  };

  return (
    <main data-testid="contact-page">
      <SEO
        title="Contact — OA Holding CO LTD."
        description="Contact OA Holding CO LTD. — Trust Company Complex, Ajeltake Road, Ajeltake Island, Majuro, Marshall Islands MH 96960. info@oaholding.co"
        path="/contact"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ContactPage",
          name: "Contact OA Holding CO LTD.",
          url: "https://oaholding.co/contact",
        }}
      />

      <section className="px-[6vw] pb-20 pt-[22vh] md:pt-[26vh]">
        <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-amber md:text-xs" data-reveal>
          Contact
        </p>
        <h1 className="mt-6 font-display text-[13vw] font-black uppercase leading-[0.9] tracking-tighter md:text-[8vw]" data-reveal>
          Let&rsquo;s move
          <br />
          <span className="text-amber">something forward.</span>
        </h1>
      </section>

      <section className="grid gap-16 border-t border-white/10 px-[6vw] py-20 md:grid-cols-2 md:gap-24 md:py-28">
        <div>
          <div data-reveal>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-smoke">Email</p>
            <a
              href="mailto:info@oaholding.co"
              className="mt-3 inline-block font-display text-2xl font-bold tracking-tight transition-colors duration-300 hover:text-amber md:text-4xl"
              data-testid="contact-email"
            >
              info@oaholding.co
            </a>
          </div>
          <div className="mt-14" data-reveal>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-smoke">Head Office</p>
            <address className="mt-4 text-sm not-italic leading-relaxed text-bone/80 md:text-base" data-testid="contact-address">
              OA Holding CO LTD.
              <br />
              Trust Company Complex
              <br />
              Ajeltake Road, Ajeltake Island
              <br />
              Majuro, Marshall Islands MH 96960
              <br />
              P.O. Box 1405
            </address>
          </div>
          <div className="mt-14" data-reveal>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-smoke">Company</p>
            <p className="mt-4 text-sm leading-relaxed text-bone/80 md:text-base">
              Global shipping &amp; software solutions.
              <br />
              Operating since 1999.
            </p>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-10" data-testid="contact-form">
          <div data-reveal>
            <label htmlFor="contact-name" className="font-mono text-[10px] uppercase tracking-[0.3em] text-smoke">
              Name
            </label>
            <input id="contact-name" data-testid="contact-name-input" required value={form.name} onChange={set("name")} className={inputCls} placeholder="Your name" />
          </div>
          <div data-reveal>
            <label htmlFor="contact-company" className="font-mono text-[10px] uppercase tracking-[0.3em] text-smoke">
              Company
            </label>
            <input id="contact-company" data-testid="contact-company-input" value={form.company} onChange={set("company")} className={inputCls} placeholder="Your company" />
          </div>
          <div data-reveal>
            <label htmlFor="contact-email" className="font-mono text-[10px] uppercase tracking-[0.3em] text-smoke">
              Email
            </label>
            <input id="contact-email" data-testid="contact-email-input" type="email" required value={form.email} onChange={set("email")} className={inputCls} placeholder="you@company.com" />
          </div>
          <div data-reveal>
            <label htmlFor="contact-interest" className="font-mono text-[10px] uppercase tracking-[0.3em] text-smoke">
              Interest
            </label>
            <select
              id="contact-interest"
              data-testid="contact-interest-select"
              value={form.interest}
              onChange={set("interest")}
              className={`${inputCls} cursor-pointer appearance-none [&>option]:bg-graphite [&>option]:text-bone`}
            >
              {INTERESTS.map((i) => (
                <option key={i} value={i}>
                  {i}
                </option>
              ))}
            </select>
          </div>
          <div data-reveal>
            <label htmlFor="contact-message" className="font-mono text-[10px] uppercase tracking-[0.3em] text-smoke">
              Message
            </label>
            <textarea id="contact-message" data-testid="contact-message-input" required rows={5} value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} placeholder="Tell us what you want to move." />
          </div>
          <div data-reveal>
            <button
              type="submit"
              data-testid="contact-submit-button"
              className="group flex items-center gap-3 border border-amber/60 px-8 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-amber transition-colors duration-300 hover:bg-amber hover:text-ink"
            >
              Send enquiry
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </button>
            <p className="mt-4 font-mono text-[10px] leading-relaxed tracking-[0.1em] text-smoke">
              Submits via your email client to info@oaholding.co
            </p>
          </div>
        </form>
      </section>
    </main>
  );
}
